// src/pages/merchant-pages/AddShippedContainerModal.js
import { useState, useContext } from "react";
import axios from "axios";

import { UserAuth } from "../../utils/AuthContext";
import { useTranslation } from "react-i18next";

import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomButton from "../../components/CustomButton";
import addIcon from "../../assets/add_icon.png";
import SubTreatmentsSection from "./SubTreatmentsSection";

import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const AddTreatmentModal = ({ setShowAddTreatmentModal, getAllTreatments }) => {
  const { t } = useTranslation("general");

  const { userData } = useContext(UserAuth);

  // treatment state
  const [errorMessage, setErrorMessage] = useState("");
  const [errorFields, setErrorFields] = useState({});
  const [loadingModal, setLoadingModal] = useState(false);
  const [treatmentName, setTreatmentName] = useState("");
  const [treatmentPrice, setTreatmentPrice] = useState(null);
  const [subTreatments, setSubTreatments] = useState([]);
  const [showSubTreatmentSection, setShowSubTreatment] = useState(false);

  // treatments
  const handleTreatmentNameChange = (event) => {
    setTreatmentName(event.target.value);
  };

  const handleTreatmentPriceChange = (event) => {
    setTreatmentPrice(
      event.target.value ? parseFloat(event.target.value) : null
    );
  };

  const handleSubTreatmentChange = (subTreatments) => {
    console.log("parent", subTreatments);
    setSubTreatments(subTreatments);
  };

  const removeSubTreatment = (index) => {
    setSubTreatments((prevSubTreatments) =>
      prevSubTreatments.filter((_, i) => i !== index)
    );
  };

  const handleShowSubTreatment = (e) => {
    e.preventDefault();
    setShowSubTreatment(!showSubTreatmentSection);
    setSubTreatments([{ subTreatmentName: "", subTreatmentPrice: null }]);
  };

  const handleCancelModal = () => {
    setTreatmentName("");
    setTreatmentPrice(null);
    setShowAddTreatmentModal(false);
  };

  const validateFields = () => {
    let errors = {};
    let hasError = false;

    if (!treatmentName.trim()) {
      errors["treatmentName"] = t("emptyInputField");
      hasError = true;
    }

    if (!showSubTreatmentSection) {
      if (
        treatmentPrice === null ||
        isNaN(treatmentPrice) ||
        treatmentPrice <= 0
      ) {
        errors["treatmentPrice"] = t("validPriceRequired");
        hasError = true;
      }
    }

    subTreatments.forEach((sub, index) => {
      if (!sub.subTreatmentName.trim()) {
        errors[`subTreatmentName-${index}`] = t("subTreatmentNameRequired");
        hasError = true;
      }
      if (
        isNaN(parseFloat(sub.subTreatmentPrice)) ||
        parseFloat(sub.subTreatmentPrice) <= 0
      ) {
        errors[`subTreatmentPrice-${index}`] = t("subTreatmentPriceRequired");
        hasError = true;
      }
    });

    setErrorFields(errors);
    return !hasError;
  };

  // function to add a treatment to the database
  const handleAddTreatment = async () => {
    if (!validateFields()) return;

    setLoadingModal(true);
    try {
      const token = userData?.token;
      const userId = parseInt(userData.userId, 10);
      const organizationId = parseInt(
        localStorage.getItem("organizationId"),
        10
      );

      // if (!userId) {
      //   console.error("User ID not found in localStorage");
      //   return;
      // }

      const payload = {
        name: treatmentName,
        treatmentPrice:
          subTreatments.length === 0 ? sanitizeNumber(treatmentPrice) : null,
        subMedicalTreatments:
          subTreatments.map((sub) => ({
            name: sub.subTreatmentName,
            price: sanitizeNumber(parseFloat(sub.subTreatmentPrice)),
          })) || [],
        organizationId,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createMedicalTreatment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        getAllTreatments(1, "");
        handleCancelModal();
      }

      setLoadingModal(false);
    } catch (error) {
      console.error("Error creating treatment:", error.response?.data || error);
      setErrorMessage(t("networkError"));
      setLoadingModal(false);
    }
  };

  const handleSendTreatmentButton = () => {
    handleAddTreatment();
  };

  return (
    <Modal
      title={t("createTreatment")}
      onClose={handleCancelModal}
      //   error={emptyInputField}
      footer={
        <>
          {loadingModal ? <Loader /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={t("confirm")}
              onClick={handleSendTreatmentButton}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleCancelModal}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </>
      }
    >
      <form className="o-column o-column--gap-size_400">
        {errorMessage && (
          <div
            className={`${"o-row o-row--horizontal-align_center o-row--vertical-align_center"} ${
              classes.flexableForm
            }`}
          >
            {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}
          </div>
        )}

        <section>
          <div className={classes.inputContainer}>
            <span>{t("treatmentName")}: </span>
            <CustomInput
              name="treatmentName"
              value={treatmentName}
              flex={true}
              onChange={(e) => handleTreatmentNameChange(e)}
              errorText={errorFields["treatmentName"]}
            />
          </div>
        </section>
        <section>
          <div
            className={`${classes.inputContainer} ${
              showSubTreatmentSection ? "disabled-area" : ""
            }`}
          >
            <span>{t("price")}: </span>
            <CustomInputWithUnit
              name="treatmentPrice"
              value={treatmentPrice}
              type="money"
              onChange={(e) => handleTreatmentPriceChange(e)}
              flex={true}
              errorText={errorFields["treatmentPrice"]}
              disabled={showSubTreatmentSection}
              unit={t("IQD")}
            />
          </div>
        </section>

        {showSubTreatmentSection ? (
          <SubTreatmentsSection
            sendDataToParent={handleSubTreatmentChange}
            errorFields={errorFields}
            validateFields={validateFields}
          />
        ) : (
          <div className="o-column o-column--vertical-align_end">
            <CustomButton
              onClick={handleShowSubTreatment}
              variant="blank"
              icon={addIcon}
              title={t("addSubTreatment")}
            />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AddTreatmentModal;
